import { useEffect, useState } from 'react';
import { tenantApi, type Tenant } from '../api/tenants';

type Props = {
  organizationId?: string;
  currentTenantId?: string;
  onSwitch?: (tenant: Tenant) => void;
};

export function TenantSelector({ organizationId, currentTenantId, onSwitch }: Props) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        setTenants(await tenantApi.list({ organization_id: organizationId, status: 'active' }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load tenants');
      } finally {
        setLoading(false);
      }
    })();
  }, [organizationId]);

  async function handleSwitch(id: string) {
    setSwitching(true);
    setError('');
    try {
      await tenantApi.switch(id);
      const t = tenants.find((x) => x.id === id);
      if (t && onSwitch) onSwitch(t);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Switch failed');
    } finally {
      setSwitching(false);
    }
  }

  if (loading) return <p className="text-sm text-slate-600">Loading tenants…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (tenants.length === 0) return <p className="text-sm text-slate-600">No active tenants.</p>;

  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">Active tenant</span>
      <select
        value={currentTenantId ?? ''}
        disabled={switching}
        onChange={(e) => void handleSwitch(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        aria-label="Select tenant"
      >
        <option value="" disabled>
          Select tenant…
        </option>
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} ({t.slug})
          </option>
        ))}
      </select>
    </label>
  );
}
